import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMediaQuery } from '@uidotdev/usehooks';
import { motion } from 'framer-motion';
import { useForm } from '@formspree/react';
import config from '../../config.json';
import { calculatePrice, getCommissionStatus } from '../../lib';
import { theme } from '../../styles';
import {
  ButtonGroup,
  ButtonCheck,
  RadioCounter,
  TextInput,
  FormRow,
  InputGroup,
  Label,
  Button,
  ButtonTextIcon,
  ButtonIcon,
  MainContent,
  Title
} from '../../components';
import { Form, FormSection, FormSeparator, SubmitWrapper, SuccessWrapper } from './style';
import { ReturnIcon } from '../../assets/svg';

const Request = () => {
  const location = useLocation();
  const commStatus = getCommissionStatus();
  let defaultRender = 'Sketch';
  const renderOptions = [];

  if (commStatus.includes('vtuber')) {
    defaultRender = 'VTuber Model';
    renderOptions.push('VTuber Model');
  }
  if (commStatus.includes('fullrender')) {
    defaultRender = 'Full Render';
    renderOptions.push('Full Render');
  }
  if (commStatus.includes('sketch')) {
    defaultRender = 'Sketch';
    renderOptions.push('Sketch');
  }
  renderOptions.reverse();

  const [render, setRender] = useState(
    location.state?.render ? location.state.render : defaultRender
  );
  const [span, setSpan] = useState(location.state?.span ? location.state.span : 'Half Body');
  const [background, setBackground] = useState(
    location.state?.background ? location.state.background : false
  );
  const [nsfw, setNsfw] = useState(location.state?.nsfw ? location.state.nsfw : false);
  const [count, setCount] = useState(location.state?.count ? location.state.count : 1);
  const [price, setPrice] = useState(0);
  const [preferredContact, setPreferredContact] = useState('Email');
  const [state, handleSubmit] = useForm('xeqbggzv');
  const isWidthSm = useMediaQuery(`only screen and (min-width: ${theme.breakpoints.width.sm})`);
  useEffect(() => {
    setPrice(calculatePrice(render, span, background, nsfw, count));
  }, [render, span, background, nsfw, count]);

  useEffect(() => {
    if (render === 'VTuber Model') {
      setBackground(false);
      setNsfw(false);
    }
    setCount(1);
  }, [render]);

  return (
    <MainContent $alignItems="center">
      {!state.succeeded && commStatus.includes('open') ? (
        <Form onSubmit={handleSubmit}>
          <Title>Request a Commmission</Title>
          <motion.p
            initial={theme.fadeIn.initial}
            animate={theme.fadeIn.animate}
            transition={theme.fadeIn.transition(theme.delayMd)}
          >
            Fill out the form for your request. If your request is accepted, I will reach out to
            you at your preferred contact method. Please read the{' '}
            <Link
              to="/terms"
              state={{ work: true }}
            >
              Work Process
            </Link>{' '}
            for more information. Please read <Link to="/prices">Prices</Link> for more
            information on commission pricing.
          </motion.p>
          <FormSection
            initial={theme.fadeIn.initial}
            animate={theme.fadeIn.animate}
            transition={theme.fadeIn.transition(theme.delayMd)}
          >
            {isWidthSm ? (
              <>
                <FormRow>
                  <TextInput
                    label="Email"
                    name="Email"
                    type="email"
                    required={preferredContact === 'Email'}
                  />
                  <InputGroup>
                    <Label>Preferred Contact Method</Label>
                    <ButtonGroup
                      options={['Email', 'Discord', 'Twitter']}
                      active={preferredContact}
                      handleChange={val => setPreferredContact(val)}
                    />
                  </InputGroup>
                </FormRow>
                <FormRow>
                  <TextInput
                    label="Discord"
                    name="Discord"
                    type="text"
                    required={preferredContact === 'Discord'}
                  />
                  <TextInput
                    label="Twitter"
                    name="Twitter"
                    type="text"
                    required={preferredContact === 'Twitter'}
                  />
                </FormRow>
              </>
            ) : (
              <>
                <InputGroup>
                  <Label>Preferred Contact Method</Label>
                  <ButtonGroup
                    options={['Email', 'Discord', 'Twitter']}
                    active={preferredContact}
                    handleChange={val => setPreferredContact(val)}
                  />
                </InputGroup>
                <TextInput
                  label="Email"
                  name="Email"
                  type="email"
                  required={preferredContact === 'Email'}
                />

                <TextInput
                  label="Discord"
                  name="Discord"
                  type="text"
                  required={preferredContact === 'Discord'}
                />
                <TextInput
                  label="Twitter"
                  name="Twitter"
                  type="text"
                  required={preferredContact === 'Twitter'}
                />
              </>
            )}
          </FormSection>
          <FormSeparator
            initial={theme.fadeIn.initial}
            animate={theme.fadeIn.animate}
            transition={theme.fadeIn.transition(theme.delayXl)}
          />
          <FormSection
            initial={theme.fadeIn.initial}
            animate={theme.fadeIn.animate}
            transition={theme.fadeIn.transition(theme.delayLg)}
          >
            {commStatus.includes('open') && (
              <ButtonGroup
                options={renderOptions}
                active={render}
                handleChange={val => setRender(val)}
              />
            )}

            <ButtonGroup
              options={['Half Body', 'Full Body']}
              active={span}
              handleChange={val => setSpan(val)}
            />
          </FormSection>
          <FormSeparator
            initial={theme.fadeIn.initial}
            animate={theme.fadeIn.animate}
            transition={theme.fadeIn.transition(theme.delayXl)}
          />
          <FormSection
            initial={theme.fadeIn.initial}
            animate={theme.fadeIn.animate}
            transition={theme.fadeIn.transition(theme.delayXl)}
          >
            {render !== 'VTuber Model' ? (
              <>
                {isWidthSm ? (
                  <>
                    <FormRow>
                      <ButtonCheck
                        label="Add Background"
                        checked={background}
                        handleChange={val => setBackground(val)}
                      />
                      <ButtonCheck
                        label="NSFW Art"
                        checked={nsfw}
                        handleChange={val => setNsfw(val)}
                      />
                    </FormRow>
                  </>
                ) : (
                  <>
                    <ButtonCheck
                      label="Add Background"
                      checked={background}
                      handleChange={val => setBackground(val)}
                    />
                    <ButtonCheck
                      label="NSFW Art"
                      checked={nsfw}
                      handleChange={val => setNsfw(val)}
                    />
                  </>
                )}
                <RadioCounter
                  label="Characters"
                  name="Amount of Characters"
                  max={3}
                  value={count}
                  handleChange={val => setCount(val)}
                />
              </>
            ) : (
              <RadioCounter
                label="Additional Toggles"
                name="Additional Toggles"
                max={4}
                value={count}
                handleChange={val => setCount(val)}
              />
            )}

            <TextInput
              label="Describe your request"
              name="Request Description"
              type="textarea"
              placeholder={config.requestDescriptionPlaceholder}
              required={true}
            />
          </FormSection>
          <SubmitWrapper
            initial={theme.fadeIn.initial}
            animate={theme.fadeIn.animate}
            transition={theme.fadeIn.transition(theme.delayXl)}
          >
            <Button
              color="brand"
              size="lg"
              type="submit"
              height={`calc(${theme.sizeH6} + (2 * ${theme.spaceMd}))`}
              width="full"
              justify="space-between"
            >
              Request
              <ButtonTextIcon>{price}</ButtonTextIcon>
            </Button>
            <p>
              The following price is an estimate. Costs are subject to change at my discretion.
            </p>
            <p>
              Please read the
              <Link
                to="/terms"
                target="blank"
              >
                {' '}
                Terms of Service{' '}
              </Link>
              before making a request.
            </p>
          </SubmitWrapper>
          <input
            type="hidden"
            name="Preferred Contact Method"
            value={preferredContact}
          />
          <input
            type="hidden"
            name="Render"
            value={render}
          />
          <input
            type="hidden"
            name="Span"
            value={span}
          />
          <input
            type="hidden"
            name="Add Background"
            value={background ? 'Yes' : 'No'}
          />
          <input
            type="hidden"
            name="NSFW"
            value={nsfw ? 'Yes' : 'No'}
          />
          <input
            type="hidden"
            name="Estimated Price"
            value={price}
          />
        </Form>
      ) : (
        <SuccessWrapper
          initial={theme.fadeIn.initial}
          animate={theme.fadeIn.animate}
          transition={theme.fadeIn.transition(0)}
        >
          <hgroup>
            {commStatus === 'full' || commStatus === 'closed' ? (
              <>
                <Title>Requests closed</Title>
                <p>
                  Commissions are currently closed and I am not taking requests. Check back
                  again soon!
                </p>
              </>
            ) : (
              <>
                <Title>Request successfully sent</Title>
                <p>
                  Thank you for your request! If accepted, I will contact you as soon as
                  possible via {preferredContact === 'Email' ? 'email' : preferredContact}.
                </p>
              </>
            )}
          </hgroup>
          <Button
            type="Link"
            style="fill"
            color="brand"
            size="lg"
            width="fit-content"
            to="/"
          >
            Return home
            <ButtonIcon>
              <ReturnIcon />
            </ButtonIcon>
          </Button>
        </SuccessWrapper>
      )}
    </MainContent>
  );
};

export default Request;
